import { Suspense, lazy, useState } from 'react';

import { useLocation } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { captureScreen } from '../lib/capture';
import { getFileName } from '../lib/file';
import { useCreateContact } from '../query/contact';
import { useCreatePreSignedUrl } from '../query/file';
import { Textarea } from './ui/textarea';

const MessageCircle = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.MessageCircle })),
);
const Send = lazy(() => import('lucide-react').then(mod => ({ default: mod.Send })));
const X = lazy(() => import('lucide-react').then(mod => ({ default: mod.X })));

export function ContactButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const pathname = location.pathname;

  const isSolvePage = /^\/course\/[^/]+\/[^/]+\/solve$/.test(pathname);

  const { mutateAsync: createPreSignedUrl, isPending: isCreatingPreSignedUrl } = useCreatePreSignedUrl({
    meta: { hideToast: true },
  });

  const { mutate: createContact, isPending: isCreatingContact } = useCreateContact();

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('메시지를 입력해주세요');
      return;
    }

    let publicUrl: string = '';
    try {
      const blob = await captureScreen();
      const file = new File([blob], getFileName('png', 'student'), { type: 'image/png' });

      const presignedRes = await createPreSignedUrl({
        prefix: 'student',
        contentType: 'image/png',
      });
      if (!presignedRes.data) return null;

      const { preSignedUrl, key } = presignedRes.data;

      const response = await fetch(preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'image/png',
        },
      });

      if (!response.ok) {
        throw new Error('S3 upload failed');
      }

      publicUrl = `https://questophia-image.s3.ap-northeast-2.amazonaws.com/${key}`;
      console.log('handleSubmit publicUrl:', publicUrl);
    } catch (error) {
      console.log('handleSubmit error:', error);
    }

    setIsSubmitting(true);
    createContact(
      {
        message,
        pathname,
        browserUrl: publicUrl,
      },
      {
        onSuccess: () => {
          toast.success('문의사항이 전송되었습니다.');
          setMessage('');
          setIsOpen(false);
          setIsSubmitting(false);
        },
        onError: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  if (isSolvePage) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" id="floating_button">
      {!isOpen && (
        <Button
          className="h-12 shadow-lg hover:shadow-xl transition-all duration-300 bg-blue-600 hover:bg-blue-700 rounded-full px-4 gap-2 flex items-center"
          onClick={() => setIsOpen(true)}
        >
          <Suspense fallback={<div className="h-5 w-5" />}>
            <MessageCircle className="h-5 w-5 shrink-0" />
          </Suspense>
          <span className="text-sm font-medium whitespace-nowrap">문의사항</span>
        </Button>
      )}

      {/* 문의사항 카드 */}
      {isOpen && (
        <Card className="w-80 shadow-xl border-0 animate-in slide-in-from-bottom-2 duration-200 bg-white">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">문의사항</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0"
              >
                <Suspense fallback={<div className="w-4 h-4" />}>
                  <X className="w-4 h-4" />
                </Suspense>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="궁금한 점이나 문의사항을 남겨주세요..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                취소
              </Button>
              <Button
                onClick={() => {
                  if (isSubmitting || isCreatingPreSignedUrl || isCreatingContact) return;
                  handleSubmit();
                }}
                disabled={isSubmitting || isCreatingPreSignedUrl || isCreatingContact}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Suspense fallback={<div className="w-4 h-4" />}>
                    <Send className="w-4 h-4 mr-2" />
                    전송
                  </Suspense>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
